import { z } from 'zod';

import { modelParameterTypes } from '../../emissionsModel/emissionsModelSchema';
import pluck from '../../utils/pluck';
import { BadDataError } from '@watershed/errors/BadDataError';

const customizationParameterSchema = z
  .object({
    name: z.string().min(1),
    type: z.enum(modelParameterTypes),
    unit: z.string().nullable().default(null),
    nullable: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.type === 'number') {
      // Require unit for number type
      if (data.unit === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Unit is required when type is 'number'",
          path: ['unit'],
        });
      }
    } else {
      // Disallow unit for non-number types
      if (data.unit !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Unit must be null when type is not 'number'",
          path: ['unit'],
        });
      }
    }
  });

const customizationSchema = z.object({
  parameters: z
    .array(customizationParameterSchema)
    .min(1)
    .refine(
      (parameters) => {
        const names = parameters.map(pluck('name'));
        return new Set(names).size === names.length;
      },
      { message: 'Parameter names must be unique' }
    ),
});

export namespace schemaUtils {
  /**
   * Represents the structured definition (schema) of a calculation
   * customization.
   */
  export type Schema = z.infer<typeof customizationSchema>;

  /**
   * Parses unknown input into a validated `Schema` object.
   */
  export function parseSchema(
    schema: unknown
  ): z.SafeParseReturnType<unknown, Schema> {
    return customizationSchema.safeParse(schema);
  }

  /**
   * Utility type that adds a parsed `Schema` field to an existing type.
   */
  export type WithParsedSchema<T, K extends keyof T> = T & {
    [k in K]: Schema;
  };

  /**
   * Takes an object and a key, parses the value at that key as a `Schema`, and
   * returns a new object with the parsed schema attached. Throws `BadDataError`
   * if parsing fails.
   */
  export function withParsedSchema<
    K extends keyof T,
    T extends { [key in K]: object },
  >(obj: T, key: K): WithParsedSchema<T, K> {
    const parseResult = parseSchema(obj[key]);
    if (!parseResult.success) {
      throw new BadDataError('Invalid schema record', {
        cause: parseResult.error,
        data: { parseResult, input: obj },
      });
    }
    return {
      ...obj,
      [key]: parseResult.data,
    };
  }
}
